// src/pages/doSurvey/DoSurvey.jsx
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchQuestions,
  updateAnswer,
} from "../../redux/slices/questionsSlice";
import { fetchAreas } from "../../redux/slices/areasSlice";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { VolumeUp, StopCircle } from "@mui/icons-material";
import ReactAudioPlayer from "react-audio-player";
import axios from "axios";

import Navbar from "../../components/navbar/Navbar";
import Footer from "../../components/navbar/Footer";
import { StepsContext } from "../../context/stepsContext";

import banner_image from "../../assets/banner_image.png";

export default function DoSurvey() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dispatch: stepsDispatch } = useContext(StepsContext);

  // Pull questions from Redux
  const questions = useSelector((state) => state.questions.questions);

  // Local state
  const [email, setEmail] = useState("");
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [errorCheck, setErrorCheck] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const theme = useTheme();
  // ---------------------------------------------
  // 1) INITIAL DATA FETCH: QUESTIONS + AREAS
  // ---------------------------------------------
  useEffect(() => {
    localStorage.removeItem("surveyRecordId");
    const fetchData = async () => {
      await dispatch(fetchQuestions());
      await dispatch(fetchAreas());
    };
    fetchData();
  }, [dispatch]);

  // ---------------------------------------------
  // 2) IF recordId EXISTS → PULL EXISTING SURVEY
  // ---------------------------------------------
  useEffect(() => {
    const fetchExistingSurveyData = async () => {
      try {
        if (!recordId || questions.length === 0) return;

        const resp = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/getSurveyData/${recordId}`
        );
        if (resp.data?.id) {
          // Save to localStorage
          localStorage.setItem("surveyRecordId", resp.data.id);
          localStorage.setItem("email", JSON.stringify(resp.data.email));

          // There are 78 questions total
          for (let i = 1; i <= 78; i++) {
            const key = `q${i}`;
            dispatch(
              updateAnswer({ questionId: key, answer: resp.data[key] || 0 })
            );
          }

          const page = resp.data.lastarea;
          if (page > 0 && page < 15) {
            navigate(`/step${page + 1}`);
          } else if (page === 15) {
            navigate("/step15");
          }
        }
      } catch (err) {
        console.error("ERROR fetching survey data:", err.message);
      }
    };

    if (recordId && questions.length > 0) {
      fetchExistingSurveyData();
    }
  }, [recordId, questions, dispatch, navigate]);

  // ---------------------------------------------
  // 3) HANDLERS
  // ---------------------------------------------
  const handleAudioToggle = () => {
    setAudioPlaying((prev) => !prev);
  };

  const handleClick = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorCheck("Please provide a valid email address.");
      return;
    }

    setErrorCheck("");
    setLoadingCheck(true);

    try {
      const resp = await axios.get(
        `${
          process.env.REACT_APP_BASE_URL
        }/checkSurveyEmail/${encodeURIComponent(email)}`
      );

      if (resp.data.already_done) {
        // Show dialog and don't allow to continue
        setDialogMessage(
          "Another person from this company has done/is doing the Bronze Survey."
        );
        setDialogOpen(true);
        setLoadingCheck(false);
        return;
      } else if (resp.data && resp.data.id) {
        // Already started/completed
        localStorage.setItem("surveyRecordId", resp.data.id);
        localStorage.setItem("email", JSON.stringify(resp.data.email));

        for (let i = 1; i <= 78; i++) {
          const key = `q${i}`;
          dispatch(
            updateAnswer({ questionId: key, answer: resp.data[key] || 0 })
          );
        }

        const page = resp.data.lastarea;
        if (page > 0 && page < 15) {
          navigate(`/step${page + 1}`);
        } else if (page === 15) {
          navigate("/result", { state: { pass: resp.data.pass } });
        }
      } else {
        // New survey
        localStorage.setItem("email", JSON.stringify(email));
        stepsDispatch({ type: "START" });
        navigate("/step1");
      }
    } catch (err) {
      console.error(err);
      setErrorCheck(
        "Something went wrong while checking your email. Please try again."
      );
    } finally {
      setLoadingCheck(false);
    }
  };

  // ---------------------------------------------
  // 4) RENDER
  // ---------------------------------------------
  return (
    <Box sx={{ minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      {/* NAVBAR */}
      <Navbar />

      <Box sx={{ height: "100%" }}>
        <img
          src={banner_image}
          alt="Banner"
          style={{ width: "100%", height: "auto" }}
        />
      </Box>

      {/* MAIN CONTENT AREA */}
      <Box
        sx={{
          py: { xs: 4, md: 4 },
          px: { xs: 2, sm: 4 },
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
          }}
        >
          <Grow in timeout={600}>
            <Box>
              {/* TITLE */}
              {/* <Box
                sx={{
                  textAlign: "center",
                  mb: 4,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    color: "#1f2937",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2.5rem" },
                    lineHeight: 1.2,
                    letterSpacing: "0.5px",
                  }}
                >
                  Investors in Sales Bronze Level Assessment
                </Typography>
              </Box> */}

              {/* MAIN CARD */}
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: 3,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                  p: { xs: 3, sm: 4, md: 6 },
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                {/* WELCOME HEADER */}
                <Typography
                  variant="h4"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "1.25rem", md: "1.75rem" },
                    lineHeight: 1.3,
                  }}
                >
                  Take the Bronze Level of the Investor in Sales
                </Typography>
                {/* EMAIL INPUT SECTION */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1.5,
                    width: "100%",
                    p: 2,
                    backgroundColor: "#f9fafb",
                    borderRadius: 2,
                    mb: 3,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.95rem",
                      color: "#374151",
                      lineHeight: 1.5,
                      m: 0,
                    }}
                  >
                    Enter your email to receive the link to the assessment
                  </Typography>
                  <TextField
                    placeholder="Enter your email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!errorCheck}
                    helperText={errorCheck}
                    size="small"
                    sx={{
                      backgroundColor: "#fff",
                      minWidth: { xs: "100%", sm: "300px" },
                      m: 0,
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    mb: 2,
                    fontSize: "1rem",
                    fontWeight: 1000,
                  }}
                >
                  Before you begin, please read the following:
                </Typography>
                {/* INSTRUCTIONS SECTIONS */}
                <Box sx={{ mb: 6, fontSize: "1rem" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "#3b82f6",
                        borderRadius: "50%",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "1rem",
                        }}
                      >
                        Answer for the sales department:
                      </Typography>
                      <Typography
                        sx={{
                          color: "#374151",
                          lineHeight: 1.6,
                          fontSize: "1rem",
                        }}
                      >
                        As a senior leader, you are completing this survey on
                        behalf of your organisation's sales department. Do not
                        base your answers solely on your direct team or those
                        reporting to you.
                      </Typography>
                    </Box>
                  </Box>

                  {/* 2) Saving Progress */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "#3b82f6",
                        borderRadius: "50%",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Saving your progress:
                      </Typography>
                      <Typography
                        sx={{
                          color: "#374151",
                          lineHeight: 1.6,
                          fontSize: "1rem",
                        }}
                      >
                        The assessment will take a while to complete. To return
                        to it after a pause or interruption and not to lose
                        work, enter your email address in the column provided.
                        Use this to pick up from the page you last completed.
                      </Typography>
                    </Box>
                  </Box>

                  {/* 3) Structure of the survey */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "#3b82f6",
                        borderRadius: "50%",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Structure of the survey:
                      </Typography>
                      <Box sx={{ pl: 2, space: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            // mb: 1,
                            gap: 1,
                          }}
                        >
                          –
                          <Typography
                            sx={{
                              color: "#374151",
                              lineHeight: 1.6,
                              fontSize: "1rem",
                            }}
                          >
                            There are 15 areas, each assessing key aspects of
                            the sales function.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          –
                          <Typography
                            sx={{
                              color: "#374151",
                              lineHeight: 1.6,
                              fontSize: "1rem",
                            }}
                          >
                            Each area contains 5 questions, focusing on specific
                            metrics and practices.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* 4) Answering format */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: "#3b82f6",
                        borderRadius: "50%",
                        mt: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "1rem",
                        }}
                      >
                        Answering format:
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            // mb: 1,
                            gap: 1,
                          }}
                        >
                          –
                          <Typography
                            sx={{
                              color: "#374151",
                              lineHeight: 1.6,
                              fontSize: "1rem",
                            }}
                          >
                            Each question uses a slider scale. Please ensure you
                            provide a response for all questions.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          –
                          <Typography
                            sx={{
                              color: "#374151",
                              fontSize: "1rem",
                              lineHeight: 1.6,
                            }}
                          >
                            Your responses will contribute to an overall
                            assessment, so completeness is essential.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <div className="flex flex-col md:flex-row justify-center items-center gap-4">
                  <Button
                    onClick={handleAudioToggle}
                    className="audio-toggle-btn w-full md:w-auto"
                    variant="contained"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      padding: "8px 30px",
                      textTransform: "none",
                      borderRadius: "999px",
                      backgroundColor: "#3F9BFD",
                      "&:hover": {
                        backgroundColor: "#3F9BFD",
                      },
                    }}
                    startIcon={audioPlaying ? <StopCircle /> : <VolumeUp />}
                  >
                    {audioPlaying
                      ? "Stop Explanation"
                      : "Listen to the Explanation"}
                  </Button>

                  {audioPlaying && (
                    <ReactAudioPlayer
                      src="https://api.theispawards.com/reports/ISPAwards_introduction.mp3"
                      autoPlay
                      loop
                      controls={false}
                      onEnded={() => setAudioPlaying(false)}
                      style={{ display: "none" }}
                    />
                  )}

                  <Button
                    aria-label="ddd"
                    onClick={handleClick}
                    className="signupCVbtnSign w-full md:w-auto"
                    type="submit"
                    variant="contained"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      padding: "8px 30px",
                      textTransform: "capitalize",
                      borderRadius: "999px",
                      backgroundColor: "#3F9BFD",
                      "&:hover": {
                        backgroundColor: "#3F9BFD",
                      },
                    }}
                    disabled={!email || loadingCheck}
                    startIcon={
                      loadingCheck ? <CircularProgress size={20} /> : null
                    }
                  >
                    {loadingCheck ? "Checking..." : "Get Started"}
                  </Button>
                </div>
              </Box>
            </Box>
          </Grow>
        </Box>
      </Box>

      {/* NOTICE DIALOG */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="survey-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle
          id="survey-dialog-title"
          sx={{ fontWeight: 600, color: "#1f2937" }}
        >
          Notice
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: 1 }}>
            {dialogMessage}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* FOOTER */}
      <Footer />
    </Box>
  );
}
